import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import Docs from "@/Docs";
import { Privacy, Terms } from "@/Legal";
import { motion } from "framer-motion";
import {
  Box, Calculator, Truck, Copy, Check, ArrowRight, Bot, Phone,
  Smartphone, Braces, ShieldCheck, Waypoints, ServerOff,
} from "lucide-react";

const MCP_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/mcp`;
const CATALOG_URL = "https://www.phsinc.com/pallet/plastic-pallets/";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.emergent.pallethub.wqcb7t";

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const inView = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const CAPABILITIES = [
  {
    icon: Box,
    title: "Browse pallets",
    desc: "Query 100+ plastic pallet SKUs by keyword or category — rackable, stackable, nestable, export, hygienic, heavy-duty, spill-containment — with live specs, static/dynamic load ratings, materials and pricing.",
    tools: ["search_pallets", "get_pallet_details", "popular_pallets"],
    testId: "capability-browse",
  },
  {
    icon: Calculator,
    title: "Get quotes",
    desc: "File a real quote request with the PHS sales team from inside the conversation — itemized subtotal, estimated freight to any US ZIP, and lead time. Payment happens via Stripe checkout, never in chat.",
    tools: ["request_quote", "my_quotes"],
    testId: "capability-quotes",
  },
  {
    icon: Truck,
    title: "Track & reorder",
    desc: "Passwordless sign-in via an emailed one-time code. Check order status, carrier, tracking number and ETA with a full shipment timeline — or reorder your last order at previous pricing with fresh freight.",
    tools: ["request_login_code", "login_with_code", "my_orders", "track_order", "reorder"],
    testId: "capability-track",
  },
];

const TOOLS = [
  { name: "search_pallets", type: "READ", desc: "Keyword + category search across the full catalog with in-memory spec filtering on name, description, pallet type and material." },
  { name: "get_pallet_details", type: "READ", desc: "Complete engineering spec sheet for one SKU — dimensions, static/dynamic capacity, deck type, material, weight, price, availability." },
  { name: "popular_pallets", type: "READ", desc: "Real best sellers aggregated from order history, paired with a buying guide that maps pallet types to use cases." },
  { name: "request_quote", type: "WRITE", desc: "Creates a pending quote request with itemized subtotal, live freight estimate to any US ZIP and lead time. Reviewed by the PHS sales team." },
  { name: "request_login_code", type: "WRITE", desc: "Starts passwordless sign-in: emails the account holder a single-use 6-digit code (10-minute expiry, enumeration-safe response)." },
  { name: "login_with_code", type: "WRITE", desc: "Exchanges the emailed code for a scoped session token. No passwords are ever typed into chat." },
  { name: "my_orders", type: "READ", desc: "Authenticated order history for the signed-in customer — status, totals and line items." },
  { name: "track_order", type: "READ", desc: "Live shipment tracking: status, carrier, tracking number, ETA and the full event timeline from confirmed to delivered." },
  { name: "my_quotes", type: "READ", desc: "The customer's quote pipeline with statuses, totals and line items." },
  { name: "reorder", type: "WRITE", desc: "One-step repeat purchase: re-prices the last order at previous unit pricing with a fresh freight estimate and emails a payment link." },
];

const TECH = [
  { icon: Waypoints, title: "Model Context Protocol", desc: "An open standard adopted by ChatGPT and Claude. Stateless streamable HTTP transport over JSON-RPC 2.0 — no session pinning, horizontally scalable." },
  { icon: Braces, title: "Typed structured outputs", desc: "Every tool publishes a JSON outputSchema built from typed models, so assistants receive validated structured content — never free-text guesses." },
  { icon: ShieldCheck, title: "Safety annotations", desc: "All 10 tools carry readOnly / destructive / idempotent hints. Writes are strictly additive — nothing is ever modified or deleted from chat." },
  { icon: ServerOff, title: "Zero data at rest", desc: "A pure gateway: every call proxies to the PHS production API over HTTPS. No local database, no stored credentials, no payments in chat." },
];

const STATS = [
  { value: "1977", label: "Year founded", testId: "stat-founded" },
  { value: "50", label: "States served", testId: "stat-states" },
  { value: "100+", label: "Pallet SKUs in catalog", testId: "stat-skus" },
  { value: "7", label: "Pallet categories", testId: "stat-categories" },
];

const NETWORK_POINTS = [
  { city: "Fresno", x: 8, y: 45 },
  { city: "San Francisco", x: 5, y: 38 },
  { city: "Los Angeles", x: 10, y: 56 },
  { city: "Kansas City", x: 47, y: 44 },
  { city: "Elgin", x: 58, y: 29, hq: true },
  { city: "Chicago", x: 61, y: 32 },
  { city: "Louisville", x: 64, y: 47 },
  { city: "Atlanta", x: 68, y: 60 },
  { city: "Charlotte", x: 73, y: 53 },
  { city: "Jacksonville", x: 73, y: 70 },
  { city: "Boston", x: 88, y: 20 },
];

const DELIVERY_POINTS = [
  { x: 12, y: 15 }, { x: 18, y: 62 }, { x: 33, y: 42 }, { x: 45, y: 64 },
  { x: 52, y: 21 }, { x: 58, y: 70 }, { x: 80, y: 28 },
];

const LANES = [
  { d: "M48,225 Q450,40 844,119", packet: { dur: "8s", begin: "0s" } },
  { d: "M96,332 Q380,430 652,356", packet: { dur: "7s", begin: "-3s" } },
  { d: "M77,267 Q260,215 451,261", packet: { dur: "5s", begin: "-1.5s" } },
  { d: "M451,261 Q580,350 700,415", packet: { dur: "4.5s", begin: "-2s" } },
  { d: "M556,172 Q320,180 96,332", packet: { dur: "6.5s", begin: "-5s" } },
  { d: "M585,190 Q715,110 844,119", packet: { dur: "4s", begin: "-0.8s" } },
  { d: "M614,279 Q625,320 652,356" },
  { d: "M700,314 Q790,200 844,119", packet: { dur: "4s", begin: "-2.6s" } },
  { d: "M652,356 Q670,390 700,415" },
  { d: "M556,172 Q640,220 700,314", packet: { dur: "5s", begin: "-3.7s" } },
  { d: "M96,332 Q60,200 115,89", packet: { dur: "5s", begin: "-1.2s" }, oneWay: true },
  { d: "M77,267 Q120,330 173,368", packet: { dur: "4s", begin: "-2.9s" }, oneWay: true },
  { d: "M451,261 Q445,320 431,379", packet: { dur: "3.5s", begin: "-0.5s" }, oneWay: true },
  { d: "M556,172 Q430,190 316,249", packet: { dur: "5s", begin: "-4.2s" }, oneWay: true },
  { d: "M585,190 Q540,140 498,124", packet: { dur: "3.5s", begin: "-1.9s" }, oneWay: true },
  { d: "M614,279 Q580,350 556,415", packet: { dur: "4.5s", begin: "-3.3s" }, oneWay: true },
  { d: "M700,314 Q740,230 767,166", packet: { dur: "4s", begin: "-2.2s" }, oneWay: true },
];

const SHIPMENT_DOTS = [
  { x: 20, y: 30 }, { x: 30, y: 60 }, { x: 40, y: 25 }, { x: 52, y: 55 },
  { x: 57, y: 70 }, { x: 66, y: 22 }, { x: 76, y: 38 }, { x: 82, y: 30 },
  { x: 35, y: 42 }, { x: 25, y: 48 }, { x: 70, y: 44 }, { x: 45, y: 65 },
];

const NetworkMap = () => (
  <div data-testid="network-map">
    <div className="relative">
      <img
        src="/us-map.svg"
        alt="PHS Pallets US shipping network"
        className="w-full opacity-[0.35]"
      />
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 958.69 592.79"
        preserveAspectRatio="none"
        data-testid="freight-lanes"
      >
        {LANES.map((l, i) => (
          <path key={`lane-${i}`} className="lane" d={l.d} vectorEffect="non-scaling-stroke" />
        ))}
        {LANES.filter((l) => l.packet).map((l, i) => (
          <circle key={`pkt-${i}`} r="3.5" className="packet">
            <animateMotion
              path={l.d}
              dur={l.packet.dur}
              begin={l.packet.begin}
              repeatCount="indefinite"
              calcMode="linear"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.12;0.88;1"
              dur={l.packet.dur}
              begin={l.packet.begin}
              repeatCount="indefinite"
            />
          </circle>
        ))}
      </svg>
      {DELIVERY_POINTS.map((d, i) => (
        <span
          key={`del-${i}`}
          className="absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-slate-300 bg-slate-200"
          style={{ left: `${d.x}%`, top: `${d.y}%` }}
        />
      ))}
      {NETWORK_POINTS.map((p) => (
        <div key={p.city} className="absolute" style={{ left: `${p.x}%`, top: `${p.y}%` }}>
          <span className={p.hq ? "map-dot map-dot-hq" : "map-dot map-dot-stock"} />
          <span className={`absolute left-2.5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[9px] font-semibold ${p.hq ? "text-red-500 uppercase tracking-wider" : "text-slate-500"}`}>
            {p.hq ? `Headquarters · ${p.city}` : p.city}
          </span>
        </div>
      ))}
      {SHIPMENT_DOTS.map((d, i) => (
        <span
          key={i}
          className="map-shipment"
          style={{ left: `${d.x}%`, top: `${d.y}%`, animationDelay: `${(i * 0.7) % 5}s` }}
        />
      ))}
    </div>
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4" data-testid="live-network-strip">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-600">
        <span className="flex items-center gap-1.5 font-semibold uppercase tracking-wider text-red-500">
          <span className="live-blink h-1.5 w-1.5 rounded-full bg-red-500" /> Live network
        </span>
        <span><strong className="text-slate-900">50</strong> states served</span>
        <span><strong className="text-slate-900">Residential &amp; commercial</strong> delivery</span>
        <span><strong className="text-slate-900">Any US ZIP</strong> freight-quoted</span>
        <span><strong className="text-slate-900">3–7 day</strong> typical transit</span>
      </div>
    </div>
    <div className="mt-3 flex flex-wrap items-center gap-5 text-xs text-slate-500">
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Headquarters</span>
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#00599c]" /> Stocking location</span>
      <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full border border-slate-300 bg-slate-200" /> Regional coverage</span>
    </div>
  </div>
);

const EndpointBlock = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_ENDPOINT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("copy failed", e);
    }
  };
  return (
    <div className="endpoint-block" data-testid="mcp-endpoint-block">
      <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-slate-700/60">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
          MCP endpoint · Streamable HTTP
        </span>
        <button
          data-testid="copy-endpoint-btn"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00599c]"
        >
          {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <code data-testid="mcp-endpoint-url" className="block px-5 py-4 font-mono text-sm sm:text-[15px] text-sky-300 break-all">
        {MCP_ENDPOINT}
      </code>
    </div>
  );
};

const StoreButtons = () => (
  <div className="flex flex-wrap items-center gap-3">
    <a
      data-testid="play-store-btn"
      href={PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block rounded-lg transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white"
    >
      <img src="/badge-google-play.webp" alt="Get it on Google Play" className="h-14 w-auto" />
    </a>
    <a
      data-testid="app-store-btn"
      href="https://apps.apple.com/us/app/phs-pallet/id6788104215"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block rounded-lg transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white"
    >
      <img src="/badge-app-store.webp" alt="Download on the App Store" className="h-14 w-auto" />
    </a>
  </div>
);

const ToolRow = ({ tool, index }) => (
  <motion.div
    {...inView}
    transition={{ duration: 0.35, delay: (index % 2) * 0.06 }}
    data-testid={`tool-ref-${tool.name}`}
    className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-5"
  >
    <span
      className={`mt-0.5 shrink-0 rounded px-2 py-0.5 font-mono text-[10px] font-semibold tracking-wider ${
        tool.type === "READ" ? "bg-emerald-50 text-emerald-700" : "bg-[#00599c]/10 text-[#00599c]"
      }`}
    >
      {tool.type === "READ" ? "READ-ONLY" : "WRITE"}
    </span>
    <div>
      <code className="font-mono text-sm font-semibold text-slate-900">{tool.name}</code>
      <p className="mt-1 text-sm leading-relaxed text-slate-600">{tool.desc}</p>
    </div>
  </motion.div>
);

function Landing() {
  useEffect(() => {
    if (window.location.pathname === "/chatgpt-app-submission.json") {
      window.history.replaceState({}, "", "/");
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/chatgpt-app-submission.json`;
    }
  }, []);

  return (
    <div className="page-root" data-testid="landing-page">
      <header className="mx-auto max-w-6xl px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="brand-mark">
          <img src="/premier-logo.png" alt="Premier Handling Solutions" className="h-9 w-auto" data-testid="brand-logo" />
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-mono text-slate-500">MCP Gateway</span>
        </div>
        <nav className="flex items-center gap-5">
          <Link
            data-testid="header-docs-link"
            to="/docs"
            className="text-sm font-medium text-slate-600 hover:text-[#00599c] transition-colors"
          >
            Docs
          </Link>
          <a
            data-testid="header-catalog-link"
            href={CATALOG_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline text-sm font-medium text-slate-600 hover:text-[#00599c] transition-colors"
          >
            Pallet catalog
          </a>
          <a
            data-testid="header-phs-link"
            href="https://phsinc.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[#00599c] hover:text-[#00477d] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00599c] rounded"
          >
            phsinc.com ↗
          </a>
        </nav>
      </header>

      <main>
        {/* Hero */}
        <section className="hero-grid-bg">
          <div className="mx-auto max-w-6xl px-6 pt-16 pb-20 md:pt-24 md:pb-28">
            <motion.p {...fadeUp} transition={{ duration: 0.5 }} className="heading-font text-sm font-bold uppercase tracking-[0.2em] text-[#00599c]">
              PHS Pallets
            </motion.p>
            <motion.h1
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.08 }}
              data-testid="hero-heading"
              className="heading-font mt-4 max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 leading-[1.08]"
            >
              AI-enabled plastic pallet ordering
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.16 }}
              data-testid="hero-subline"
              className="mt-5 max-w-2xl text-lg text-slate-600"
            >
              Ask ChatGPT or Claude to search 100+ plastic pallet SKUs, compare load ratings, request
              freight-included quotes to any US ZIP, and track shipments end-to-end — served over the
              Model Context Protocol, straight from live PHS production inventory.
            </motion.p>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.24 }} className="mt-8 flex flex-wrap items-center gap-5">
              <a
                data-testid="hero-catalog-link"
                href={CATALOG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-[#00599c] px-6 py-3 text-sm font-semibold text-white hover:bg-[#00477d] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00599c]"
              >
                Browse the pallet catalog <ArrowRight size={16} />
              </a>
              <span className="inline-flex items-center gap-2 text-sm text-slate-500">
                <Bot size={16} className="text-[#00599c]" /> Works with ChatGPT &amp; Claude connectors
              </span>
            </motion.div>
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.32 }} className="mt-10 max-w-2xl">
              <EndpointBlock />
              <p className="mt-2.5 text-xs text-slate-500">
                Paste this URL into your ChatGPT or Claude connector settings to add PHS Pallets as a tool.
              </p>
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="border-t border-slate-200 bg-white">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <h2 className="heading-font text-base md:text-lg font-semibold tracking-tight text-slate-900" data-testid="how-it-works-heading">
              How it works
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-600">
              Ten tools exposed over the Model Context Protocol, proxying to live PHS production data.
            </p>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {CAPABILITIES.map((c, i) => (
                <motion.div
                  key={c.title}
                  {...inView}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  data-testid={c.testId}
                  className="rounded-xl border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                >
                  <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#00599c]/10 text-[#00599c]">
                    <c.icon size={22} />
                  </span>
                  <h3 className="heading-font mt-5 text-lg font-semibold tracking-tight text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {c.tools.map((t) => (
                      <code key={t} className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600">
                        {t}
                      </code>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Tool reference */}
        <section className="border-t border-slate-200 bg-slate-50" data-testid="tools-reference-section">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
            <p className="heading-font text-xs font-bold uppercase tracking-[0.2em] text-[#00599c]">Tool reference</p>
            <h2 className="heading-font mt-3 max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="tool-reference-heading">
              Every tool, fully typed.
            </h2>
            <p className="mt-3 max-w-2xl text-base text-slate-600">
              Each tool publishes a JSON outputSchema and MCP safety annotations, so assistants know
              exactly what they can read, what they can write, and what they can never touch.
            </p>
            <div className="mt-10 grid gap-4 md:grid-cols-2">
              {TOOLS.map((t, i) => (
                <ToolRow key={t.name} tool={t} index={i} />
              ))}
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {TECH.map((t, i) => (
                <motion.div key={t.title} {...inView} transition={{ duration: 0.4, delay: i * 0.06 }} className="rounded-lg border border-slate-200 bg-white p-6">
                  <t.icon size={20} className="text-[#00599c]" />
                  <h3 className="heading-font mt-3 text-sm font-semibold text-slate-900">{t.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Heritage */}
        <section className="border-t border-slate-200 bg-white" data-testid="heritage-section">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
            <NetworkMap />
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-[#00599c]/20 bg-[#00599c]/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#00599c]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00599c]" /> Operated by Premier Handling Solutions
              </span>
              <h2 className="heading-font mt-5 text-3xl sm:text-4xl font-bold tracking-tight leading-tight" data-testid="heritage-heading">
                <span className="text-slate-900">Since 1977,</span>
                <br />
                <span className="text-[#00599c]">from coast to coast.</span>
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
                PHS Pallets is the plastic pallet division of{" "}
                <a href="https://phsinc.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 underline decoration-[#00599c] decoration-2 underline-offset-2 hover:text-[#00599c] transition-colors" data-testid="heritage-phs-link">
                  Premier Handling Solutions
                </a>{" "}
                — a trusted partner to American manufacturers, distributors, and Fortune 500 logistics
                teams, delivering residential and commercial orders nationwide.
                From stackable and rackable workhorses to ISPM-15-exempt export pallets and
                washdown-ready hygienic designs for food and pharma, we stock real inventory, quote
                real freight, and now serve it all to AI assistants over an open protocol.
              </p>
              <div className="mt-7 grid grid-cols-2 gap-4 max-w-md">
                {STATS.map((s, i) => (
                  <motion.div
                    key={s.label}
                    {...inView}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                    data-testid={s.testId}
                    className="rounded-xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                  >
                    <p className="heading-font text-3xl font-bold text-[#00599c]">{s.value}</p>
                    <p className="mt-1.5 text-sm font-medium text-slate-500">{s.label}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-4">
                <a
                  data-testid="heritage-catalog-link"
                  href={CATALOG_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#00599c] px-6 py-3 text-sm font-semibold text-white hover:bg-[#00477d] transition-colors"
                >
                  Explore plastic pallets <ArrowRight size={16} />
                </a>
                <a
                  data-testid="phone-link"
                  href="tel:+18004042311"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:border-[#00599c] hover:text-[#00599c] transition-colors"
                >
                  <Phone size={15} /> (800) 404-2311
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile apps */}
        <section className="bg-black" data-testid="apps-section">
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-24 grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:items-center">
            <div>
              <p className="heading-font text-xs font-bold uppercase tracking-[0.2em] text-sky-400">
                <Smartphone size={14} className="mr-1.5 inline -mt-0.5" /> Mobile apps
              </p>
              <h2 className="heading-font mt-4 max-w-xl text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="apps-heading">
                The full PHS Pallets experience, in your pocket.
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300">
                The PHS Pallets app brings the entire catalog to Android and iOS — browse and compare specs,
                build a cart, get instant freight-quoted pricing, pay securely with Stripe checkout,
                and follow every shipment with live carrier tracking. The same production backend
                powers the app, this MCP gateway, and your AI conversations.
              </p>
              <ul className="mt-6 grid max-w-xl gap-2.5 text-sm text-sky-100 sm:grid-cols-2">
                {["100+ SKUs with full spec sheets", "Freight-quoted pricing to your ZIP", "Secure Stripe checkout", "Live order & shipment tracking"].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} className="mt-0.5 shrink-0 text-sky-300" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:justify-self-end">
              <StoreButtons />
              <p className="mt-4 text-xs text-slate-400">
                Available now on Google Play and the App Store.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-6 py-12 grid gap-10 md:grid-cols-3">
          <div>
            <img src="/premier-logo.png" alt="Premier Handling Solutions" className="h-8 w-auto" />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
              AI-enabled plastic pallet ordering from Premier Handling Solutions — serving American
              supply chains since 1977.
            </p>
          </div>
          <div>
            <p className="heading-font text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Explore</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><a data-testid="footer-catalog-link" href={CATALOG_URL} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-[#00599c] transition-colors">Plastic pallet catalog</a></li>
              <li><a data-testid="footer-phs-link" href="https://phsinc.com" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-[#00599c] transition-colors">phsinc.com</a></li>
              <li><a data-testid="footer-play-link" href={PLAY_URL} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-[#00599c] transition-colors">PHS Pallets on Google Play</a></li>
              <li><a data-testid="footer-appstore-link" href="https://apps.apple.com/us/app/phs-pallet/id6788104215" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-[#00599c] transition-colors">PHS Pallets on the App Store</a></li>
            </ul>
          </div>
          <div>
            <p className="heading-font text-xs font-bold uppercase tracking-[0.15em] text-slate-400">For AI &amp; developers</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link data-testid="footer-docs-link" to="/docs" className="text-slate-600 hover:text-[#00599c] transition-colors">API documentation</Link></li>
              <li><code className="font-mono text-xs text-slate-600 break-all">{MCP_ENDPOINT}</code></li>
              <li><a data-testid="footer-submission-link" href={`${process.env.REACT_APP_BACKEND_URL}/api/chatgpt-app-submission.json`} className="text-slate-600 hover:text-[#00599c] transition-colors">Download ChatGPT submission JSON</a></li>
              <li><a data-testid="footer-phone-link" href="tel:+18004042311" className="text-slate-600 hover:text-[#00599c] transition-colors">(800) 404-2311</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <p className="mx-auto max-w-6xl px-6 py-5 text-sm text-slate-500 flex flex-wrap items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} PHS Pallets · Premier Handling Solutions · MCP Gateway</span>
            <span className="flex gap-4">
              <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-[#00599c] transition-colors">Privacy Policy</Link>
              <Link data-testid="footer-terms-link" to="/terms" className="hover:text-[#00599c] transition-colors">Terms of Service</Link>
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/docs" element={<Docs />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </BrowserRouter>
  );
}
